import React from 'react'
import { colors } from './fieldTheme'
import { referenceLabel } from './fieldReference'
import { summarize } from './fieldSummary'
import { guestName } from './fieldStays'
import { ChevronLeft, ChevronRight, CheckIcon, AlertIcon, InfoIcon } from './FieldIcons'
import { FlowChip, SectionTitle, StickyActionBar, SaveInventoryButton } from './FieldUI'

// Récapitulatif avant l'enregistrement (canvas W10c, relecture seulement). Décisions 26, 28, 30, 34 et 48 :
// commentaires, photos et interventions viennent après l'enregistrement, sur l'écran Suite à donner.
// Une ligne d'écart ouvre son thème pour corriger le comptage ; les objets déjà absents
// sont une information pour l'équipe, sans action (décision 30).

const TONES = {
	ok: { background: '#DCFCE7', color: '#14532D', circle: '#15803D' },
	gap: { background: '#FEE2E2', color: '#7F1D1D', circle: '#B91C1C' }
}

function Banner({ banner })
{
	const tone = TONES[banner.tone]
	return (
		<div role="status" style={{ margin: '24px 20px 0', display: 'flex', alignItems: 'center', gap: 14, padding: 20, borderRadius: 18, background: tone.background, color: tone.color }}>
			<span aria-hidden="true" style={{ width: 48, height: 48, borderRadius: 999, background: tone.circle, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
				{banner.tone === 'ok' ? <CheckIcon size={28} color="#FFFFFF" /> : <AlertIcon />}
			</span>
			<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
				<span style={{ fontSize: 24, fontWeight: 800 }}>{banner.title}</span>
				<span style={{ fontSize: 17, fontWeight: 700 }}>{banner.text}</span>
			</span>
		</div>
	)
}

function Hint({ children, top = 12 })
{
	return <p style={{ padding: top + 'px 20px 0', fontSize: 15, fontWeight: 500, color: colors.textSecondary }}>{children}</p>
}

function ObjectLines({ item })
{
	return (
		<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4, lineHeight: 1.3 }}>
			<span lang="fr" style={{ fontSize: 19, fontWeight: 800, overflowWrap: 'break-word', hyphens: 'auto', WebkitHyphens: 'auto' }}>{item.object.name}</span>
			<span style={{ fontSize: 17, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{item.line}</span>
			<span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{item.theme.name}</span>
		</span>
	)
}

function Group({ children })
{
	const items = React.Children.toArray(children)
	return (
		<div style={{ margin: '0 20px', background: colors.surface, borderRadius: 18, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
			{items.map((child, index) => (
				<div key={child.key || index} style={{ borderTop: index > 0 ? '1px solid ' + colors.separator : 0 }}>{child}</div>
			))}
		</div>
	)
}

export default function FieldReview({ type, pitch, stay, client, themes, counts, reference, now, saving, saveError, onBack, onOpenTheme, onSave })
{
	const summary = summarize(type, themes, counts, reference, now)
	const name = guestName(client)
	const identity = stay ? [name, 'Séjour n° ' + stay.stay_number].filter(Boolean).join(', ') : null

	return (
		<div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, minHeight: 56, padding: '4px 20px 0' }}>
				<button
					type="button"
					className="field-solid"
					onClick={onBack}
					style={{ backgroundImage: 'none', backgroundColor: '#E8EBF5', display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 48, padding: '0 18px 0 10px', borderRadius: 999, border: 0, color: colors.primary, fontSize: 17, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', flex: 'none' }}
				>
					<ChevronLeft />
					Thèmes
				</button>
				<FlowChip type={type} location={pitch.location} />
			</div>

			<div style={{ padding: '24px 20px 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
				<h1 style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.2, color: colors.text, paddingBottom: 2 }}>Récapitulatif</h1>
				{identity && <span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{identity}</span>}
				<span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{referenceLabel(reference, now)}</span>
			</div>

			<Banner banner={summary.banner} />
			{summary.banner.hint && <Hint>{summary.banner.hint}</Hint>}
			{summary.gaps.length === 0 && <Hint>Un objet manque finalement ? Revenez aux thèmes pour le corriger.</Hint>}

			{summary.gaps.length > 0 && (
				<React.Fragment>
					<SectionTitle>{summary.gapsTitle + ' (' + summary.gaps.length + ')'}</SectionTitle>
					<Group>
						{summary.gaps.map(item => (
							<button
								key={item.object.id}
								type="button"
								className="field-row"
								aria-label={'Corriger le comptage : ' + item.object.name}
								onClick={() => onOpenTheme(item.theme.id)}
								style={{ backgroundImage: 'none', backgroundColor: colors.surface, minHeight: 104, width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '16px 16px 16px 20px', border: 0, color: colors.text, textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer' }}
							>
								<ObjectLines item={item} />
								<span style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', minHeight: 34, padding: '0 12px', borderRadius: 999, background: '#FEE2E2', color: '#7F1D1D', fontSize: 15, fontWeight: 800 }}>{item.badge}</span>
								<ChevronRight />
							</button>
						))}
					</Group>
					{summary.othersText && <Hint>{summary.othersText}</Hint>}
				</React.Fragment>
			)}

			{summary.missing.length > 0 && (
				<React.Fragment>
					<h2 style={{ fontSize: 15, fontWeight: 700, color: colors.textSecondary, letterSpacing: '.04em', textTransform: 'uppercase', padding: '32px 20px 4px' }}>{summary.missingTitle + ' (' + summary.missing.length + ')'}</h2>
					<p style={{ padding: '0 20px 12px', fontSize: 15, fontWeight: 500, color: colors.textSecondary }}>{summary.missingHint}</p>
					<Group>
						{summary.missing.map(item => (
							<div key={item.object.id} style={{ minHeight: 104, display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px' }}>
								<ObjectLines item={item} />
								<span style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 34, padding: '0 12px 0 8px', borderRadius: 999, background: '#FFEDD5', color: '#7C2D12', fontSize: 15, fontWeight: 800 }}>
									<InfoIcon size={18} />
									{item.badge}
								</span>
							</div>
						))}
					</Group>
				</React.Fragment>
			)}

			<div aria-hidden="true" style={{ height: 32, flex: 'none' }} />
			<StickyActionBar>
				<SaveInventoryButton saving={saving} saveError={saveError} onSave={onSave} />
				<p style={{ fontSize: 15, fontWeight: 500, color: colors.textSecondary, textAlign: 'center' }}>Ensuite : commentaires, photos et interventions.</p>
			</StickyActionBar>
		</div>
	)
}
